import { PaymentMethodGateway } from '@app/_proto/Protos/payments';
import type { SponsorPlanItem } from '@app/_proto/Protos/sponsors';
import type { SubscriptionItem } from '@app/_proto/Protos/subscriptions';
import { CHAMPION_EVENT_SOURCES } from '@app/domains/champion/analytics/posthog/constants';
import ChampionOtherGatewayBox, { WebGateways } from '@app/domains/champion/components/ChampionOtherGatewayBox';
import ChampionPlans from '@app/domains/champion/components/ChampionPlans';
import ChampionPlanWithDialog from '@app/domains/champion/containers/ChampionPlanWithDialog';
import type { ClassNameType } from '@app/domains/common/types';
import { noop } from '@app/domains/common/utils';
import { getOneofValue } from '@app/libs/grpc';
import clsx from 'clsx';
import type { HTMLAttributes, PropsWithChildren } from 'react';
import { twMerge } from 'tailwind-merge';

type Props = {
    novelId: number;
    novelName: string;
    championPlans: SponsorPlanItem[] | undefined;
    activeChampionSubscription: SubscriptionItem | undefined;
    advanceChapterCount: number;
    onClickMore: () => void;
    onSubscribePlan: () => void;
};

export default function AdvancedChapterLockView({
    novelId,
    novelName,
    championPlans,
    activeChampionSubscription,
    advanceChapterCount,
    onClickMore,
    onSubscribePlan,
}: Props) {
    const isSubscribingNovel = !!activeChampionSubscription;
    const subscribingPlan = getOneofValue(activeChampionSubscription?.plan?.plan, 'sponsor');

    const isSubscriptionWeb = WebGateways.includes(
        activeChampionSubscription?.paymentGateway ?? PaymentMethodGateway.None
    );

    return (
        <Wrapper data-posthog-params={JSON.stringify({ on: CHAMPION_EVENT_SOURCES.AdvancePaywallLayer })}>
            <Logo />
            <div className="mb-16">
                <Title className="mb-4" title={novelName} />
                <Description isSubscribingNovel={isSubscribingNovel} />
            </div>
            <div className="mb-20 text-center">
                <AdvanceChapters advanceChapterCount={advanceChapterCount} />
            </div>
            {activeChampionSubscription && !isSubscriptionWeb ? (
                <ChampionOtherGatewayBox gateway={activeChampionSubscription.paymentGateway} />
            ) : (
                <>
                    {isSubscribingNovel && subscribingPlan && (
                        <div data-testid="subscribing-plan" className="mb-12">
                            <div className="mb-20 flex justify-center">
                                <ChampionPlanWithDialog
                                    plan={subscribingPlan}
                                    currentSubscription={activeChampionSubscription}
                                    onSelectPlan={onSubscribePlan}
                                    handleProcessingStateChange={noop}
                                />
                            </div>
                            <div className="text-center">
                                <h3 className="font-set-sb16 text-gray-t1 sm:font-set-sb20">
                                    Choose a Champion tier to upgrade to
                                </h3>
                            </div>
                        </div>
                    )}
                    <div>
                        <ChampionPlans
                            novelId={novelId}
                            sponsorPlans={championPlans}
                            onSelectPlan={onSubscribePlan}
                            currentSubscription={activeChampionSubscription ?? null}
                            gapX={16}
                            gapY={12}
                            maxColumn={2}
                        />
                    </div>
                    <div className="py-13 text-center sm:py-[14.5px]">
                        <SeeAllTiersButton onClick={onClickMore} />
                    </div>
                </>
            )}
        </Wrapper>
    );
}

const Wrapper = ({ children, ...props }: PropsWithChildren<HTMLAttributes<HTMLElement>>) => {
    return (
        <section
            className="shadow-chapter-lock -mx-20 max-w-[800px] rounded-[28px] bg-gradient-ww-advanced-chapter px-20 pt-24 dark:bg-gray-850 dark:bg-none sm:mx-auto sm:px-40"
            {...props}
        >
            {children}
        </section>
    );
};

const Logo = () => {
    return (
        <div className="text-center">
            <img className="inline-block w-140" src="/images/champion-logo@3x.png" alt="champion-logo" />
        </div>
    );
};

const Title = ({ className, title }: ClassNameType & { title: string }) => {
    return (
        <div className={twMerge(clsx('text-center', className))}>
            <h2 className="font-set-b24 text-gray-t1 sm:font-set-b26">{title}</h2>
        </div>
    );
};

const Description = ({ isSubscribingNovel }: { isSubscribingNovel: boolean }) => {
    return (
        <div className="font-set-m14 whitespace-pre-line text-center text-gray-desc sm:font-set-m15">
            {isSubscribingNovel ? (
                <p>
                    {
                        'You are already a Champion subscriber!\nIf you want to unlock additional advance chapters, you need to upgrade your subscription.'
                    }
                </p>
            ) : (
                <p>{'You need a Champion subscription to this story\nto access advance chapters in advance.'}</p>
            )}
        </div>
    );
};

const AdvanceChapters = ({ advanceChapterCount }: { advanceChapterCount: number }) => {
    return (
        <div className="inline-block rounded-100 bg-gray-200 px-12 py-8 shadow-[inset_0_1px_2px_rgba(0,0,0,0.1)] dark:bg-gray-900">
            <span className="font-set-b15 mr-4 text-blue-600">{advanceChapterCount}</span>
            <span className="font-set-m13 text-gray-t3 sm:font-set-m14">Advance Chapters</span>
        </div>
    );
};

const SeeAllTiersButton = ({ onClick }: { onClick?: () => void }) => {
    return (
        <button className="font-set-sb15 text-gray-desc" onClick={onClick}>
            See all Tiers
        </button>
    );
};
